import merge from 'lodash/merge'
import { action, computed, makeObservable, observable } from 'mobx'
import { dependsOn, isPending, trackedAction } from 'mobx-async'

import arriereBackoffice, {
  FridgePackageOrderStatus,
  OpCaseCreationOptions,
  ServiceKind,
  ServiceRequestInfoInput,
  ServiceRequestStatus,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert } from '@avantstay/backoffice-core'

import { SidePanelStore, SidePanelType } from '@new/__global/stores/SidePanelStore'
import {
  isStatusActionRequired,
  isStatusCancelled,
  isStatusUnpaid,
} from '@new/domains/bookings/screens/valueAddedServices/_definitions'
import { sortServiceRequestsByCreationDate } from '@new/domains/bookings/screens/valueAddedServices/_utils'
import { ServiceFieldProps } from '@new/domains/bookings/screens/valueAddedServices/CreateMultipleServices/CreateServices.types'
import { ServiceOptionsResponse } from '@new/domains/bookings/screens/valueAddedServices/CreateMultipleServices/ServiceOptions.types'
import {
  IRequestArbitraryServiceParams,
  IRequestBasicServiceParams,
  IRequestCheckTimeServiceParams,
  IRequestItemBasedServiceParams,
  IRequestMidStayCleaningServiceParams,
  IRequestPoolHeatingServiceParams,
  ISendPaymentLinkParams,
} from '@new/domains/bookings/stores/_types'
// eslint-disable-next-line import/no-cycle
import { BookingPaymentStore } from '@new/domains/bookings/stores/BookingPaymentStore'
import { BookingStore } from '@new/domains/bookings/stores/BookingStore'
// eslint-disable-next-line import/no-cycle
import { RefundStore } from '@new/domains/bookings/stores/RefundStore'
import { BookingServiceRequestForManagePayment } from '@new/domains/payments/ManageServicesPayments/PaymentForm.types'

import {
  AdjustRequestedServiceParams,
  InternallyAvailableService,
  MakePurchaseParams,
  RequestFridgeStockingServiceParams,
} from '@/services/arriere/types/BookingServices.types'
import { trackError } from '@/tracking/customEventReport'
import {
  BookingServiceRequest,
  BookingServiceSubtotal,
} from '@/types/servicesTypes/BookingServices.types'
import bookingServices, {
  getAvailableServices,
  getBookingServicesRequestsForCancellation,
} from '@arriere/modules/bookingServices'
// eslint-disable-next-line import/no-cycle
import { UiStore } from '@stores/UiStore'

// TODO: find a way to fix those dependecy cycle problems
// eslint-disable-next-line import/no-cycle

class _BookingValueAddedServicesStore {
  servicesArray: ServiceFieldProps[] | [] = []

  temporaryCreatedServices: BookingServiceRequestForManagePayment[] | null = null

  serviceRequestIdToEditRate: string = ''

  constructor() {
    makeObservable(this, {
      servicesArray: observable,
      temporaryCreatedServices: observable,
      serviceRequestIdToEditRate: observable,
      bookingServicesRequests: computed,
      bookingServicesSubtotal: computed,
      nonApprovedBookingServicesRequests: computed,
      bookingServicesRequestsForCancellation: computed,
      bookingServicesRequestsForManagePayment: computed,
      bookingServiceRequestToEditRate: computed,
      availableServices: computed,
      getBookingHomeId: computed,
      isNewRequestPending: computed,
      setServicesArray: action.bound,
      setTemporaryCreatedServices: action.bound,
      openVASNewServicesDrawer: action.bound,
      openVASManagePaymentDrawer: action.bound,
      closeVASDrawers: action.bound,
      openEditServiceRateDrawer: action.bound,
      closeEditServiceRateDrawer: action.bound,
    })

    this.adjustRequestedService = trackedAction<any>(this.adjustRequestedService.bind(this))
    this.makeCharge = trackedAction<any>(this.makeCharge.bind(this))
    this.createMultipleServicesRequest = trackedAction<any>(
      this.createMultipleServicesRequest.bind(this),
    )
    this.cancelServiceRequest = trackedAction<any>(this.cancelServiceRequest.bind(this))
    this.rejectServiceRequest = trackedAction<any>(this.rejectServiceRequest.bind(this))
    this.confirmServiceRequest = trackedAction<any>(this.confirmServiceRequest.bind(this))
    this.updateFridgeStockingStatus = trackedAction<any>(this.updateFridgeStockingStatus.bind(this))
    this.sendBatchPaymentLink = trackedAction<any>(this.sendBatchPaymentLink.bind(this))
    this.requestPoolHeatingService = trackedAction<any>(this.requestPoolHeatingService.bind(this))
    this.requestFridgeStockingService = trackedAction<any>(
      this.requestFridgeStockingService.bind(this),
    )
    this.requestMidStayCleaningService = trackedAction<any>(
      this.requestMidStayCleaningService.bind(this),
    )
    this.requestItemBasedService = trackedAction<any>(this.requestItemBasedService.bind(this))
    this.requestCheckTimeService = trackedAction<any>(this.requestCheckTimeService.bind(this))
    this.requestBasicService = trackedAction<any>(this.requestBasicService.bind(this))
    this.requestArbitraryService = trackedAction<any>(this.requestArbitraryService.bind(this))
    this.getSalesForceCaseCreationRules = trackedAction<any>(
      this.getSalesForceCaseCreationRules.bind(this),
    )
    this.getServicePriceBreakdown = trackedAction<any>(this.getServicePriceBreakdown.bind(this))
  }

  get bookingServicesRequests(): Promise<BookingServiceRequest[]> {
    dependsOn(
      BookingStore.booking,
      RefundStore.refundPayments,
      BookingPaymentStore.cancelPaymentIfAwaiting,
      this.cancelServiceRequest,
      this.confirmServiceRequest,
      this.createMultipleServicesRequest,
      this.rejectServiceRequest,
      this.requestArbitraryService,
      this.requestBasicService,
      this.requestCheckTimeService,
      this.requestFridgeStockingService,
      this.requestItemBasedService,
      this.requestMidStayCleaningService,
      this.requestPoolHeatingService,
      this.sendBatchPaymentLink,
      this.makeCharge,
      this.updateFridgeStockingStatus,
      this.adjustRequestedService,
    )

    return BookingStore.booking
      .then(booking => {
        if (booking?.id) {
          return bookingServices.getBookingServicesRequests(booking.id)
        }

        return undefined
      })
      .then(requests => sortServiceRequestsByCreationDate(requests || []))
  }

  get bookingServicesSubtotal(): Promise<BookingServiceSubtotal> {
    dependsOn(
      BookingStore.booking,
      RefundStore.refundPayments,
      BookingPaymentStore.cancelPaymentIfAwaiting,
      this.cancelServiceRequest,
      this.confirmServiceRequest,
      this.createMultipleServicesRequest,
      this.rejectServiceRequest,
      this.requestArbitraryService,
      this.requestBasicService,
      this.requestCheckTimeService,
      this.requestFridgeStockingService,
      this.requestItemBasedService,
      this.requestMidStayCleaningService,
      this.requestPoolHeatingService,
      this.sendBatchPaymentLink,
      this.makeCharge,
      this.updateFridgeStockingStatus,
      this.adjustRequestedService,
    )

    return BookingStore.booking.then(booking => {
      if (booking?.id) {
        return bookingServices.getBookingServicesRequestsSubtotal(booking.id)
      }

      return 0
    })
  }

  get nonApprovedBookingServicesRequests() {
    return this.bookingServicesRequests.then(requests =>
      requests.filter(request =>
        [ServiceRequestStatus.requested, ServiceRequestStatus.settled].includes(request.status),
      ),
    )
  }

  get bookingServicesRequestsForCancellation() {
    return BookingStore.booking.then(booking => {
      if (booking?.id) {
        return getBookingServicesRequestsForCancellation(booking.id)
      }
      return Promise.resolve([])
    })
  }

  get bookingServicesRequestsForManagePayment(): Promise<BookingServiceRequestForManagePayment[]> {
    return this.bookingServicesRequests.then(services =>
      services
        .map(service => {
          const { status, paymentInfo } = service

          const isCreatedService = isStatusActionRequired(status, paymentInfo.status)
          const isUnpaidService = paymentInfo.paymentId
            ? isStatusUnpaid(paymentInfo.paymentId, paymentInfo.status)
            : false

          return merge({}, service, {
            isCreatedService,
            isUnpaidService,
          })
        })
        .filter(
          ({ status, isCreatedService, isUnpaidService }) =>
            !isStatusCancelled(status) && (isCreatedService || isUnpaidService),
        ),
    )
  }

  get bookingServiceRequestToEditRate(): Promise<BookingServiceRequest | undefined> {
    void [this.serviceRequestIdToEditRate]

    return this.bookingServicesRequests.then(services =>
      services.find(service => service.id === this.serviceRequestIdToEditRate),
    )
  }

  /**
   * Returns the list of available services for the current booking
   */
  get availableServices(): Promise<InternallyAvailableService[]> {
    return BookingStore.booking.then(booking => {
      return booking?.id ? getAvailableServices(booking.id) : Promise.resolve([])
    })
  }

  get getBookingHomeId(): Promise<string | undefined> {
    return BookingStore.booking.then(booking => booking?.home?.id)
  }

  getServiceOptions = async (
    service: InternallyAvailableService,
  ): Promise<ServiceOptionsResponse> => {
    const booking = await BookingStore.booking

    if (!service.id) return Promise.reject(new Error('ServiceId is required.'))

    if (!booking?.id) return Promise.reject(new Error('BookingId is required.'))

    switch (service.kind) {
      // Arbitrary and basic do not have any options
      case ServiceKind.arbitrary:
        return Promise.resolve({ data: null })

      case ServiceKind.basic:
        return bookingServices.getBasicServicePrice(booking.id, service.id)

      case ServiceKind.checkTime:
        return bookingServices.getCheckTimeOptions(booking.id, service.id)

      case ServiceKind.itemBased:
        return bookingServices.getItemBasedOptions(booking.id, service.id)

      case ServiceKind.midStayCleaning:
        return bookingServices.getMidStayCleaningOptions(booking.id, service.id)

      case ServiceKind.poolHeating:
        return bookingServices.getPoolHeatingOption(booking.id, service.id)

      case ServiceKind.stockedFridge:
        return bookingServices.getFridgeStockingPackages(booking.id, service.id)

      case ServiceKind.datesBased:
        return bookingServices.getDateBasedServiceOption(booking.id, service.id)

      default:
        return Promise.reject(new Error('ServiceKind not supported.'))
    }
  }

  get isNewRequestPending(): boolean {
    return (
      isPending(this.requestArbitraryService) ||
      isPending(this.requestBasicService) ||
      isPending(this.requestCheckTimeService) ||
      isPending(this.requestItemBasedService) ||
      isPending(this.requestMidStayCleaningService) ||
      isPending(this.requestPoolHeatingService) ||
      isPending(this.requestFridgeStockingService) ||
      isPending(this.sendBatchPaymentLink) ||
      isPending(this.createMultipleServicesRequest) ||
      isPending(this.makeCharge)
    )
  }

  setServicesArray(services: ServiceFieldProps[]): void {
    this.servicesArray = services
  }

  setTemporaryCreatedServices(
    services: BookingServiceRequestForManagePayment[] | null = null,
  ): void {
    this.temporaryCreatedServices = services
  }

  openVASNewServicesDrawer(): void {
    SidePanelStore.setCurrentSidePanel(SidePanelType.valueAddedServicesNewServices)
  }

  openVASManagePaymentDrawer(): void {
    SidePanelStore.setCurrentSidePanel(SidePanelType.valueAddedServicesManagePayment)
  }

  closeVASDrawers(): void {
    UiStore.setShouldDisplayFilterSideBar(false)
    SidePanelStore.setCurrentSidePanel()
  }

  async openEditServiceRateDrawer(requestId: string) {
    if (SidePanelStore.currentSidePanel && this.serviceRequestIdToEditRate === requestId) {
      return
    }

    if (SidePanelStore.currentSidePanel && this.serviceRequestIdToEditRate !== requestId) {
      this.serviceRequestIdToEditRate = ''
      await this.closeVASDrawers()
    }

    this.serviceRequestIdToEditRate = requestId
    SidePanelStore.setCurrentSidePanel(SidePanelType.paymentAndServicesEditServiceRate)
  }

  closeEditServiceRateDrawer(): void {
    this.serviceRequestIdToEditRate = ''
    this.closeVASDrawers()
  }

  async getServicePriceBreakdown(serviceId: string, netPrice = 0, securityDeposit?: number) {
    const booking = await BookingStore.booking

    if (!booking?.id) return Promise.resolve(undefined)

    return bookingServices.getServicePriceBreakdown(
      booking.id,
      serviceId,
      netPrice,
      securityDeposit,
    )
  }

  async getSalesForceCaseCreationRules(
    serviceId: string,
  ): Promise<OpCaseCreationOptions | undefined> {
    const booking = await BookingStore.booking

    if (!booking?.id) return Promise.resolve(undefined)

    const { salesforceCaseCreation, showDueDate, showDescription } =
      await bookingServices.getSalesForceCaseCreationRules({
        bookingId: booking.id,
        serviceId,
      })
    return { salesforceCaseCreation, showDueDate, showDescription }
  }

  async requestArbitraryService(options: IRequestArbitraryServiceParams): Promise<string> {
    const booking = await BookingStore.booking

    if (!booking?.id) {
      return Promise.reject(new Error('Booking ID is required to request arbitrary service.'))
    }

    return bookingServices.requestArbitraryService({
      bookingId: booking.id,
      ...options,
    })
  }

  async requestBasicService(options: IRequestBasicServiceParams): Promise<string> {
    const booking = await BookingStore.booking

    if (!booking?.id) {
      return Promise.reject(new Error('Booking ID is required to request basic service.'))
    }

    return bookingServices.requestBasicService({
      bookingId: booking.id,
      ...options,
    })
  }

  async requestCheckTimeService(options: IRequestCheckTimeServiceParams): Promise<string> {
    const booking = await BookingStore.booking

    if (!booking?.id) {
      return Promise.reject(new Error('Booking ID is required to request check time service.'))
    }

    return bookingServices.requestCheckTimeService({
      bookingId: booking.id,
      ...options,
    })
  }

  async requestItemBasedService(options: IRequestItemBasedServiceParams): Promise<string> {
    const booking = await BookingStore.booking

    if (!booking?.id) {
      return Promise.reject(new Error('Booking ID is required to request item based service.'))
    }

    return bookingServices.requestItemBasedService({
      bookingId: booking.id,
      ...options,
    })
  }

  async requestMidStayCleaningService(
    options: IRequestMidStayCleaningServiceParams,
  ): Promise<string> {
    const booking = await BookingStore.booking

    if (!booking?.id) {
      return Promise.reject(
        new Error('Booking ID is required to request mid-stay cleaning service.'),
      )
    }

    return bookingServices.requestMidStayCleanService({
      bookingId: booking.id,
      ...options,
    })
  }

  async requestFridgeStockingService(options: RequestFridgeStockingServiceParams): Promise<string> {
    const booking = await BookingStore.booking

    if (!booking?.id) {
      return Promise.reject(new Error('Booking ID is required to request fridge stocking service.'))
    }

    return bookingServices.requestFridgeStocking({
      bookingId: booking.id,
      ...options,
    })
  }

  async requestPoolHeatingService(options: IRequestPoolHeatingServiceParams): Promise<string> {
    const booking = await BookingStore.booking

    if (!booking?.id) {
      return Promise.reject(new Error('Booking ID is required to request pool heating service.'))
    }

    return bookingServices.requestPoolHeating({
      bookingId: booking.id,
      ...options,
    })
  }

  async sendBatchPaymentLink({
    requestsIds,
    email,
    phone,
  }: ISendPaymentLinkParams): Promise<boolean> {
    const booking = await BookingStore.booking

    if (!booking?.id) {
      return Promise.reject(new Error('Booking ID is required to send payment link.'))
    }

    const success = await bookingServices.sendBatchPaymentLink(
      requestsIds,
      booking.guestName,
      email,
      phone,
    )
    return success
      ? Promise.resolve(true)
      : Promise.reject(new Error('We were not able to send the payment link.'))
  }

  async updateFridgeStockingStatus(
    status: FridgePackageOrderStatus,
    serviceRequestId: string,
    packages: string[],
  ) {
    return arriereBackoffice.mutations.booking({
      updateStockedFridgeOrderStatus: {
        __args: {
          serviceRequestId,
          packages,
          status,
        },
      },
    })
  }

  async confirmServiceRequest(requestId: string): Promise<boolean> {
    const error = new Error('We were not able to confirm this request.')

    try {
      const success = await bookingServices.confirmServiceRequest(requestId)
      return success ? Promise.resolve(true) : Promise.reject(error)
    } catch {
      return Promise.reject(error)
    }
  }

  async rejectServiceRequest(requestId: string): Promise<boolean> {
    const error = new Error('We were not able to reject this request.')

    try {
      const success = await bookingServices.rejectServiceRequest(requestId)
      return success ? Promise.resolve(true) : Promise.reject(error)
    } catch {
      return Promise.reject(error)
    }
  }

  async cancelServiceRequest(requestId: string) {
    const error = new Error('We were not able to cancel this request.')

    try {
      const success = await bookingServices.cancelServiceRequest(requestId)
      BookingStore.updateBookingValuesFromServer()
      return success ? Promise.resolve(true) : Promise.reject(error)
    } catch {
      return Promise.reject(error)
    }
  }

  async createMultipleServicesRequest({
    servicesInput,
  }: {
    servicesInput: ServiceRequestInfoInput[]
  }): Promise<{ requestServices: string[] }> {
    const booking = await BookingStore.booking

    if (!booking?.id) {
      return Promise.reject(new Error('Booking ID is required to create services request.'))
    }

    const requestIds = await bookingServices.requestMultipleServices({
      bookingId: booking.id,
      servicesInput,
    })
    return requestIds
  }

  async makeCharge({
    bookingId,
    requestIds,
    email,
    phone,
    guestName,
    cardId,
    amount,
    wholesalePayment,
    useTravelCredit,
    depositIds,
    readerId,
  }: MakePurchaseParams): Promise<boolean | ErrorConstructor> {
    try {
      const success = await bookingServices.makePurchase({
        bookingId,
        requestIds,
        email: email?.trim() || undefined,
        phone: phone?.trim() || undefined,
        guestName,
        cardId,
        amount,
        wholesalePayment,
        useTravelCredit,
        depositIds,
        readerId,
      })

      if (success?.makePurchase) {
        BookingStore.updateBookingValuesFromServer()
      }

      return success?.makePurchase
    } catch (error) {
      const latestError = Array.isArray(error) ? error[error.length - 1] : error

      const message = latestError ? latestError.message.split('\n')[0] : error

      Alert.Error(`Error when creating services payment: ${message}`)

      trackError('VAS: There was an error when paying', {
        requestIds,
        wholesalePayment,
        error,
      })
      throw new Error(message)
    }
  }

  async adjustRequestedService({
    requestId,
    adjustment,
    reason,
    executeRefund,
  }: AdjustRequestedServiceParams) {
    try {
      const success = await bookingServices.adjustRequestedService({
        requestId,
        adjustment,
        reason,
        executeRefund,
      })

      return success
        ? Promise.resolve(true)
        : Promise.reject(new Error('We were not able to adjust requested service rate'))
    } catch (error) {
      throw new Error(String(error))
    }
  }
}

export const BookingValueAddedServicesStore = new _BookingValueAddedServicesStore()
